import React, { useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS } from "../contract";
import useSavedViews from "../hooks/useSavedViews";
import CrmMenu from "../ui/CrmMenu";
import CrmButton from "../ui/CrmButton";
import CrmDialog from "../ui/CrmDialog";
import CrmInput from "../ui/CrmInput";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const NAME_MAX = 80;

/**
 * "Saved views ▾" (spec): apply a saved filter; "Save current view" prompts for a name.
 * Views are shared by the practice (owner and helpers), and recalculate live — a view
 * stores the FILTERS, never the rows.
 */
export default function SavedViewsMenu({ filters, sort, onApply }) {
  const t = useT("crm");
  const tc = useT("common");
  const { views, save } = useSavedViews();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError(t("views.errNameRequired")); return; }
    setBusy(true);
    try {
      const view = await save(trimmed, filters, sort);
      // The view just saved IS the one on screen: it is in force from now on (exports name it).
      if (view?.id) onApply(view);
      toast.success(t("views.saved", { name: trimmed }));
      setNaming(false);
      setName("");
    } catch (e) {
      if (apiErrorCode(e) === CRM_ERRORS.viewNameTaken) setError(t("errors.viewNameTaken"));
      else toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const items = [
    ...views.map((v) => ({ key: v.id, label: v.name, onSelect: () => onApply(v), testId: CRM.clientsView(v.id) })),
    ...(views.length ? [{ separator: true }] : []),
    { key: "save", label: t("views.saveCurrent"), onSelect: () => { setError(""); setNaming(true); }, testId: CRM.clientsViewSave },
  ];

  return (
    <>
      <CrmMenu
        align="start"
        items={items}
        trigger={(
          <CrmButton variant="outline" data-testid={CRM.clientsViews}>
            {t("views.menu")} <span aria-hidden="true" className="text-[10px]">▾</span>
          </CrmButton>
        )}
      />
      <CrmDialog
        open={naming}
        onOpenChange={setNaming}
        title={t("views.saveTitle")}
        size="sm"
        footer={(
          <>
            <CrmButton variant="link" onClick={() => setNaming(false)}>{t("common.cancel")}</CrmButton>
            <CrmButton onClick={submit} disabled={busy} data-testid={CRM.clientsViewConfirm}>{t("common.save")}</CrmButton>
          </>
        )}
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <label htmlFor="crm-view-name" className={LABEL}>{t("views.name")}</label>
          <CrmInput id="crm-view-name" value={name} maxLength={NAME_MAX} invalid={!!error}
            onChange={(e) => { setName(e.target.value); setError(""); }} data-testid={CRM.clientsViewName} />
          {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
        </form>
      </CrmDialog>
    </>
  );
}
